import React, { cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LearnerAuthShell from "./learnerAuthShell";
import { resolveLmsShortName } from "./_learnerConfig";
import { learnerPortalInfoApi } from "./_learnerActions";

const LmsTenantGate = ({ children }) => {
  const { t } = useTranslation();
  const lmsShortName = useMemo(() => resolveLmsShortName(), []);
  const [state, setState] = useState({
    loading: true,
    valid: false,
    message: "",
    portalInfo: null,
  });

  useEffect(() => {
    let alive = true;

    const checkPortal = async () => {
      if (!lmsShortName) {
        // Leave `message` empty; the invalid view shows a localized fallback.
        setState({ loading: false, valid: false, message: "" });
        return;
      }

      try {
        const res = await learnerPortalInfoApi({ lmsShortName });
        const { status, data, message } = res?.data || {};
        if (!alive) return;
        setState({
          loading: false,
          valid: status === 200,
          // The backend message (already localized) wins; otherwise the invalid
          // view falls back to a localized string at render time.
          message: status === 200 ? "" : message || "",
          portalInfo: status === 200 ? data || null : null,
        });
      } catch (err) {
        if (!alive) return;
        setState({
          loading: false,
          valid: false,
          message: err?.response?.data?.message || "",
          portalInfo: null,
        });
      }
    };

    checkPortal();
    return () => {
      alive = false;
    };
  }, [lmsShortName]);

  if (state.loading) {
    const checking = t("learner.lms.gate.checking", "Checking link");
    return (
      <LearnerAuthShell title={checking} heading={checking}>
        <div className="text-center py-4">
          <i className="fas fa-circle-notch fa-spin"></i>
        </div>
      </LearnerAuthShell>
    );
  }

  if (!state.valid) {
    const notFound = t("learner.lms.gate.notFound", "Learning link not found");
    return (
      <LearnerAuthShell
        title={notFound}
        heading={notFound}
        subheading={
          state.message ||
          t(
            "learner.lms.gate.invalidLink",
            "This learning link is not valid. Please check the URL."
          )
        }
      >
        <div className="error-text text-center pb-2" role="alert">
          {t(
            "learner.lms.gate.notAvailable",
            "Link is wrong or this organisation is not available."
          )}
        </div>
      </LearnerAuthShell>
    );
  }

  return isValidElement(children)
    ? cloneElement(children, { portalInfo: state.portalInfo })
    : children;
};

export default LmsTenantGate;
